import React, {useMemo} from "react";
import {useDeskStore} from "../../api/DeskStore";
import {useFloorStore} from "../../api/FloorStore";
import {useNeighbourhoodStore} from "../../api/NeighbourhoodStore";
import {RemoteValue, useRemoteValue} from "../../util/RemoteValue";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {Page, StorePageCount} from "../../components/page/Page";
import {DeskTable} from "./DeskTable";
import {deskFilterSerializer, DeskTableFilter, filterDesks} from "./DeskTableFilter";
import {useFilter} from "../../util/FilterSerializer";
import {DeskModelImpl} from "./DeskModel";
import {useDeskModelStore} from "../../api/DeskModelStore";
import {VisitDataCoverageWarning} from "../employee-visit-report/VisitDataCoverageWarning";

export default function DesksPage() {
    const desks = useDeskStore(state => state.remoteValue);
    const floors = useFloorStore(state => state.remoteValue);
    const neighbourhoods = useNeighbourhoodStore(state => state.remoteValue);
    const resolvedFloors = useRemoteValue(floors, []);
    const resolvedNeighbourhoods = useRemoteValue(neighbourhoods, []);
    const serializer = useMemo(
        () => deskFilterSerializer(resolvedNeighbourhoods),
        [resolvedNeighbourhoods],
    );
    const [filter, setFilter] = useFilter(serializer);

    const model = useDeskModelStore(filter.dateRange);

    const filtered = useMemo(
        () => RemoteValue
            .combine({desks, floors, model})
            .map(({desks, floors, model}) => {
                const filteredDesks = filterDesks(desks, filter, model);
                return {
                    model,
                    rows: DeskModelImpl.rows(filteredDesks, floors),
                    length: filteredDesks.length,
                };
            }),
        [filter, desks, floors, model],
    );

    return (
        <Page
            header="Desks"
            counter={<StorePageCount totalValue={desks} value={filtered}/>}
            description="All office desks."
            filter={
                <DeskTableFilter
                    filter={filter}
                    setFilter={setFilter}
                    floors={resolvedFloors}
                    neighbourhoods={resolvedNeighbourhoods}
                />
            }
        >
            <LoadingData
                totalValue={desks}
                value={filtered}
                emptyMessage="No desks found."
                noMatchMessage="No desks match the current filters. Try adjusting your filter criteria."
                defaultErrorMessage="Failed to load desks. Please try again later."
            >{({model, rows}) => (
                <>
                    <VisitDataCoverageWarning dates={model.visitDates} dateRange={filter.dateRange}/>
                    <DeskTable model={model} rows={rows}/>
                </>
            )}</LoadingData>
        </Page>
    );
}
